import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Workflow, CalendarClock, MessageSquareText, UserPlus, Database,
  Star, BookOpen, Bot, Plug, Check, Loader2, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const capabilities = [
  { icon: Workflow, title: "Website Lead-Capture Systems", desc: "Turn visitors into organized, followed-up leads automatically." },
  { icon: CalendarClock, title: "Appointment Scheduling", desc: "Let clients book time without the back-and-forth." },
  { icon: MessageSquareText, title: "Email & Text Follow-Up", desc: "Automated, on-brand reminders so no lead goes cold." },
  { icon: UserPlus, title: "Client Intake & Onboarding", desc: "Smooth, consistent onboarding from first contact." },
  { icon: Database, title: "CRM & Lead-Tracking Workflows", desc: "Know exactly where every lead and client stands." },
  { icon: Star, title: "Review-Request Automation", desc: "Ask happy clients for reviews at the right moment." },
  { icon: BookOpen, title: "Business SOPs & Knowledge Bases", desc: "Document your process so your business runs without you." },
  { icon: Bot, title: "AI-Assisted Administrative Support", desc: "Offload repetitive admin tasks to smart workflows." },
  { icon: Plug, title: "Zapier, Make & n8n Consulting", desc: "Connect the tools you already use into one system." },
];

export default function AIAutomation() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "", website: "",
    business_type: "", challenge: "", preferred_time: "",
  });
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide at least your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        form_type: "ai-automation",
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_category: "AI Automation",
        source_page: "/ai-automation",
        payload: {
          business_name: form.business_name,
          website: form.website,
          business_type: form.business_type,
          challenge: form.challenge,
          preferred_time: form.preferred_time,
        },
      });
      setDone(true);
      toast.success("Thanks! We'll reach out to schedule your strategy call.");
    } catch {
      toast.error("Something went wrong. Please try again or call 201-253-7674.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="ai-automation-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">AI Automation for Small Businesses</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Spend Less Time Chasing Tasks. Spend More Time Growing Your Business.
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              Save time and improve follow-up with better lead intake, scheduling, client reminders, CRM setup, review requests, and AI-assisted workflows.
            </p>
            <Button onClick={() => document.getElementById("automation-form")?.scrollIntoView({ behavior: "smooth" })} className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium" data-testid="ai-hero-cta">
              Book an Automation Strategy Call
            </Button>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">What We Automate</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">Systems That Work While You Work</h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">Practical automation tailored to how your business actually runs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c, i) => (
              <Card key={c.title} className="service-card h-full hover-lift" data-testid={`ai-cap-${i}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center mb-6">
                    <c.icon className="w-7 h-7 text-bronze" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-navy mb-3">{c.title}</h3>
                  <p className="font-body text-gray-600">{c.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="automation-form" className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-3">Book an Automation Strategy Call</h2>
            <p className="font-body text-gray-600">Tell us about your business and your biggest workflow challenge.</p>
          </div>
          {done ? (
            <Card className="bg-bone border-0" data-testid="ai-success">
              <CardContent className="p-10 text-center">
                <Check className="w-14 h-14 text-green-600 mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-navy mb-2">Thanks, {form.name.split(" ")[0]}!</h3>
                <p className="font-body text-gray-600">We've received your inquiry and will reach out to schedule your strategy call. A confirmation email is on its way.</p>
              </CardContent>
            </Card>
          ) : (
          <form onSubmit={submit} className="bg-bone rounded-2xl p-6 md:p-10 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ai-name">Name *</Label><Input id="ai-name" data-testid="ai-name" value={form.name} onChange={(e) => up("name", e.target.value)} /></div>
              <div><Label htmlFor="ai-business">Business Name</Label><Input id="ai-business" data-testid="ai-business" value={form.business_name} onChange={(e) => up("business_name", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ai-email">Email *</Label><Input id="ai-email" type="email" data-testid="ai-email" value={form.email} onChange={(e) => up("email", e.target.value)} /></div>
              <div><Label htmlFor="ai-phone">Phone</Label><Input id="ai-phone" data-testid="ai-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="ai-website">Website</Label><Input id="ai-website" data-testid="ai-website" value={form.website} onChange={(e) => up("website", e.target.value)} placeholder="https://" /></div>
              <div><Label htmlFor="ai-type">Type of Business</Label><Input id="ai-type" data-testid="ai-type" value={form.business_type} onChange={(e) => up("business_type", e.target.value)} /></div>
            </div>
            <div><Label htmlFor="ai-challenge">Biggest Workflow Challenge</Label><Textarea id="ai-challenge" data-testid="ai-challenge" rows={3} value={form.challenge} onChange={(e) => up("challenge", e.target.value)} /></div>
            <div><Label htmlFor="ai-time">Preferred Consultation Time</Label><Input id="ai-time" data-testid="ai-time" value={form.preferred_time} onChange={(e) => up("preferred_time", e.target.value)} placeholder="e.g. Weekday afternoons" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium" data-testid="ai-submit">
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</> : <>Book Strategy Call <ArrowRight className="ml-2 w-5 h-5" /></>}
            </Button>
          </form>
          )}
        </div>
      </section>
    </div>
  );
}
